import { useCallback, useMemo, useState } from 'react';
import { Upload } from 'antd';
import { FileUpload as DSFileUpload } from '@dextech-ai/dex-design-system';
import ModalTemplate from '@/dsComponents/ModalTemplate';

// One uid per File for the whole session, so the DS list keys stay stable across renders.
const uids = new WeakMap();
let uidCounter = 0;

const getUid = (file) => {
	if (file?.uid) return file.uid;
	if (!file || typeof file !== 'object') return `ds-file-${uidCounter++}`;
	if (!uids.has(file)) uids.set(file, `ds-file-${uidCounter++}`);
	return uids.get(file);
};

// File (or a server attachment `{ filename }`) -> antd UploadFile for the DS list.
export const toUploadFile = (file) => ({
	uid: getUid(file),
	name: file?.name ?? file?.filename,
	size: file?.size,
	type: file?.type,
	thumbUrl: file?.preview,
	status: 'done',
	originFileObj: file,
});

// antd UploadFile -> the File the page works with.
export const fromUploadFile = (uploadFile) => uploadFile?.originFileObj ?? uploadFile;

// Same rules as the browser's `accept` attribute: extensions, exact MIME types and `type/*`.
export const matchesAccept = (file, accept) => {
	if (!accept) return true;
	const name = (file?.name || '').toLowerCase();
	const type = (file?.type || '').toLowerCase();
	const baseType = type.replace(/\/.*$/, '');

	return accept.split(',').map(s => s.trim().toLowerCase()).filter(Boolean).some(rule => {
		if (rule.startsWith('.')) return name.endsWith(rule);
		if (rule.endsWith('/*')) return baseType === rule.replace(/\/.*$/, '');
		return type === rule;
	});
};

/**
 * DS FileUpload for the admin-ui call-sites: takes and returns plain `File`s, never sends anything over the network
 * (pages read the files themselves), and does the checks the DS leaves to the consumer (type for picked files, size,
 * count, page-specific `validate`). Rejected files are not added and their message goes to `onError`, which by
 * default opens the DS-Modal "Invalid File" popup.
 *
 * Children are the DS parts (`FileUploadDropZone`, `FileUploadTrigger`, `FileUploadList`), used as they are.
 * `dataTestId` / `className` sit on a plain wrapper div, because the DS FileUpload takes neither.
 */
const FileUpload = ({
	files = [],
	onFilesChange,
	onRemove,
	accept,
	typeErrorMessage,
	maxFileSize,
	maxFileSizeLabel,
	sizeErrorMessage,
	maxFiles,
	maxFilesErrorMessage,
	multiple = false,
	validate,
	onError,
	disabled = false,
	dataTestId,
	className,
	children,
}) => {

	const [errorMessage, setErrorMessage] = useState(null);

	const showError = useCallback((message) => {
		if (onError) return onError(message);
		setErrorMessage(message);
	}, [onError]);

	const fileList = useMemo(() => files.map(toUploadFile), [files]);

	const beforeUpload = useCallback(async (file, batch) => {
		const reject = (message) => {
			if (message) showError(message);
			return Upload.LIST_IGNORE;
		};

		// Dragged files of the wrong type never get here (antd drops them); picked ones can, via "All files".
		if (!matchesAccept(file, accept)) {
			return reject(typeErrorMessage);
		}

		if (maxFileSize && file.size > maxFileSize) {
			return reject(sizeErrorMessage ?? `File uploaded size is more than ${maxFileSizeLabel}`);
		}

		// maxFiles 1 is not a limit here: antd replaces the current file with the new one.
		if (maxFiles > 1 && files.length + batch.length > maxFiles) {
			return reject(maxFilesErrorMessage ?? `You can upload up to ${maxFiles} files`);
		}

		if (validate) {
			const message = await validate(file, files);
			if (message) return reject(message);
		}

		// false = keep the file in the list without uploading it.
		return false;
	}, [accept, typeErrorMessage, maxFileSize, maxFileSizeLabel, sizeErrorMessage, maxFiles, maxFilesErrorMessage, validate, files, showError]);

	const handleChange = useCallback((nextFileList) => {
		onFilesChange?.(nextFileList.map(fromUploadFile));
	}, [onFilesChange]);

	const handleRemove = useCallback((uploadFile) => {
		return onRemove?.(fromUploadFile(uploadFile));
	}, [onRemove]);

	return (
		<div data-testid={dataTestId} className={className}>
			<DSFileUpload
				fileList={fileList}
				onChange={handleChange}
				onRemove={handleRemove}
				accept={accept}
				maxFileSize={maxFileSize}
				maxFiles={maxFiles}
				multiple={multiple}
				disabled={disabled}
				beforeUpload={beforeUpload}
			>
				{children}
			</DSFileUpload>

			{!onError && (
				<ModalTemplate
					visible={!!errorMessage}
					hide={() => setErrorMessage(null)}
					title="Invalid File"
					message={errorMessage}
					rightFooterButtonText="Close"
				/>
			)}
		</div>
	);
};

export default FileUpload;
